// The one list of themes the console knows. The picker, the hook, the pre-paint script
// check and the layout seam all read this module and nothing else.
//
// The list is the shared theme package's registry (src/themes/package.js): same slugs, labels,
// statuses and appearances. The package paints every theme except `clairvos`. Original Clairvos
// is the console's own look: its token values live in src/styles/globals.css and are frozen by
// tests/theme/original-tokens.test.mjs. The other themes reach the console through
// src/themes/package-map.css, which maps the package's roles onto the console's existing tokens.

import { THEME_REGISTRY, WINDOW_FRAME_CAPABILITY } from './package.js'

export const DEFAULT_THEME_ID = 'clairvos'

// A role every package theme declares. If it reads empty under a package theme, that theme's CSS did not load.
export const STYLE_SENTINEL = '--surface-ground'

// What a person can choose. `system` follows the device and resolves to light or dark.
export const APPEARANCE_SETTINGS = Object.freeze(['light', 'dark', 'system'])

// Layouts a theme may ask for. `standard` is the default view of every screen.
export const LAYOUTS = Object.freeze(['standard', 'window-rail'])

// A package theme that declares the window frame asks for the window-rail layout.
// No variant is registered yet, so every screen still draws its default view.
const layoutOf = (entry) => (entry.capabilities?.includes(WINDOW_FRAME_CAPABILITY) ? 'window-rail' : 'standard')

// The package lists no default appearance. A theme with one appearance has only that one;
// for a theme with two, the first listed is used where a fallback is needed.
const fromPackage = (entry) => Object.freeze({
  id: entry.slug,
  label: entry.label,
  status: entry.status === 'available' ? 'available' : 'pending',
  appearances: Object.freeze([...entry.appearances]),
  defaultAppearance: entry.appearances[0],
  capabilities: Object.freeze({ layout: layoutOf(entry) }),
})

// Original Clairvos first, then the package's order.
const ordered = [...THEME_REGISTRY].sort((a, b) => (b.slug === DEFAULT_THEME_ID) - (a.slug === DEFAULT_THEME_ID))
export const THEMES = Object.freeze(ordered.map(fromPackage))

export function getTheme(id, themes = THEMES) {
  return themes.find((t) => t.id === id) || null
}

export function isAvailable(id, themes = THEMES) {
  const theme = getTheme(id, themes)
  return !!theme && theme.status === 'available'
}

export function availableThemes(themes = THEMES) {
  return themes.filter((t) => t.status === 'available')
}

// The table the inline script in index.html carries (it cannot import this module).
// tests/theme/prepaint.test.mjs requires the script's copy to equal this.
export function prepaintTable(themes = THEMES) {
  const table = {}
  for (const t of availableThemes(themes)) {
    table[t.id] = { appearances: [...t.appearances], defaultAppearance: t.defaultAppearance }
  }
  return table
}

// Shape problems here would otherwise show up as a blank picker or a theme that never applies.
export function assertRegistry(themes = THEMES) {
  const seen = new Set()
  for (const t of themes) {
    if (!/^[a-z][a-z0-9-]*$/.test(t.id)) throw new Error(`Theme id "${t.id}" is not a lowercase slug.`)
    if (seen.has(t.id)) throw new Error(`Theme id "${t.id}" is listed twice.`)
    seen.add(t.id)
    if (!t.label) throw new Error(`Theme "${t.id}" has no label.`)
    if (t.status !== 'available' && t.status !== 'pending') throw new Error(`Theme "${t.id}" has status "${t.status}".`)
    if (!t.appearances.length || t.appearances.some((a) => a !== 'light' && a !== 'dark')) throw new Error(`Theme "${t.id}" has bad appearances.`)
    if (!t.appearances.includes(t.defaultAppearance)) throw new Error(`Theme "${t.id}" default appearance is not one it offers.`)
    if (!LAYOUTS.includes(t.capabilities?.layout)) throw new Error(`Theme "${t.id}" asks for unknown layout "${t.capabilities?.layout}".`)
  }
  if (!isAvailable(DEFAULT_THEME_ID, themes)) throw new Error(`The default theme "${DEFAULT_THEME_ID}" must be available.`)
}

assertRegistry()
